import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { AppSharedService } from './services/app-shared.service';
import { AppStoreService } from './services/app-store.service';
import { AppInstallService } from './services/app-install.service';
import { AppUtilityService } from './services/app-utility.service';
import { AppSettingsService } from './services/app-settings.service';
import { VideoPlaylist } from '@hsi/video-playlist';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProgressbarDirective, SwitchDirective, TooltipDirective } from './directives/app.directive';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [CommonModule, RouterOutlet, FormsModule, ProgressbarDirective, SwitchDirective, TooltipDirective]
})
export class AppComponent implements OnInit {
  title = '';
  icon = '';
  installable = false;
  displayUpdate = false;
  fullscreen = false;
  sideLayerOpen = false;
  autoPlay = false;
  fullScreenVideo = false;
  _displayCaptions = false;
  _captionLanguage = 'en';
  _videoQuality = 'md';
  routeName = '';
  tennant = localStorage.getItem('company') || 'ashi';
  registration: any;
  refreshing = false;
  videoPlaylistClientRepository = new VideoPlaylist.Infrastructure.Repositories.VideoPlaylistIndexedDbClientRepository();
  showProgressBar = false;
  copyright: number = new Date().getFullYear();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public appShared: AppSharedService,
    public appInstall: AppInstallService,
    private appUtility: AppUtilityService,
    private appStore: AppStoreService,
    private appSettings: AppSettingsService
  ) {}

  ngOnInit(): void {
    this.appUtility.removeOldServiceWorker();
    // Listen for route changes
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.routeName = event.urlAfterRedirects.split('/')[2] || '';
      const activeRoute = this.getDeepestChildRoute(this.activatedRoute);
      const routeData = activeRoute.snapshot.data as { title?: string; icon?: string };
      if (routeData.title) this.appShared.setTitle(routeData.title);
      if (routeData.icon) this.appShared.setIcon(routeData.icon);
    });

    // Listen for installable event
    this.appInstall.installable$.subscribe(val => this.installable = val);

    // Listen for service worker update event
    window.addEventListener('swUpdated', (event: any) => this.updateAvailable(event), { once: true });
    window.addEventListener('swUpdated', this.updateAvailable.bind(this), { once: true });

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', () => {
      this.fullscreen = !!document.fullscreenElement;
    });

    // Listen for beforeinstallprompt
    window.addEventListener('beforeinstallprompt', (e) => {        
      console.log(`Application can be installed.`);
      this.installable = true;
      this.appInstall.installable$.next(true);
      // Prevent the mini-infobar from appearing on mobile 
      e.preventDefault(); 
      window.installEvent = e;  
    });

    // Listen for service worker controller change
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (this.refreshing) return;
        this.refreshing = true;
        window.location.reload();
      });
    }

    this.appSettings.autoPlay$.subscribe(value => { this.autoPlay = value; });
    this.appSettings.fullScreenVideo$.subscribe(value => { this.fullScreenVideo = value; });
    this.appSettings.displayCaptions$.subscribe(value => { this._displayCaptions = value; });
    this.appSettings.captionLanguage$.subscribe(value => { this._captionLanguage = value; });
    this.appSettings.videoQuality$.subscribe(value => { this._videoQuality = value; });
    this.appUtility.showProgressBar$.subscribe(val => { this.showProgressBar = val;});
    this.appShared.title.subscribe(value => { this.title = value; });
    this.appShared.icon.subscribe(value => { this.icon = value; });
  }

  processRouteRedirect(): void {
    this.router.navigate(['/', this.tennant, 'login']);
  }

  updateAvailable(event: any): void {
    this.displayUpdate = true;
    this.registration = event.detail;
  }

  refreshApp(): void {
    this.displayUpdate = false;
    // Make sure we only send a 'skip waiting' message if the SW is waiting
    if (!this.registration || !this.registration.waiting) return
    // Send message to SW to skip the waiting and activate the new SW
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  async deleteDownloadedPlaylists(): Promise<void> {
    await this.videoPlaylistClientRepository.deleteTrainingElementObjectStore();
    this.appUtility.displayMessage({
      text: "All downloaded playlists have been removed successfully.",
      actionText: "OK",
      duration: 4000
    });
    const event = new Event('syncUpdate');
    window.dispatchEvent(event);
  }

  directory(): void {
    this.router.navigate(['/', this.tennant, 'directory']);
  }

  install(): void {
    this.router.navigate(['/', this.tennant, 'install']);
  }

  toggleFullScreen(): void {
    const elem = document.documentElement;
    if (!document.fullscreenElement && !document.mozFullScreenElement &&
      !document.webkitFullscreenElement && !document.msFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if ((elem as any).msRequestFullscreen) {
        (elem as any).msRequestFullscreen();
      } else if ((elem as any).mozRequestFullScreen) {
        (elem as any).mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  }

  toggleAutoPlay(): void {
    this.appSettings.toggleAutoPlay();
  }

  toggleFullScreenVideo(): void {
    this.appSettings.toggleFullScreenVideo();
  }

  toggleDisplayCaptions(): void {
    this.appSettings.toggleDisplayCaptions(!this._displayCaptions);
  }

  setCaptionLanguage(lang: string): void {
    this.appSettings.setCaptionLanguage(lang);
    this.appSettings.toggleDisplayCaptions(true);
  }

  async setVideoQuality(quality: string): Promise<void> {
    this.appSettings.setVideoQuality(quality);
    if (this.appSettings.downloadHd() && confirm(`To update the video quality you need to delete all downloaded playlists and re-download. Continue?`)) {
      await this.deleteDownloadedPlaylists();
    } else if (this.appSettings.downloadHd()) {
      this.appUtility.displayMessage({
        text: "Video quality updated successfully, offline play may not work correctly until all playlists are re-downloaded.",
        actionText: "OK",
        duration: 6000
      });
    }
  }

  logout(): void {
    this.appShared.logout(this.appStore.authenticationService);
  }

  private getDeepestChildRoute(route: ActivatedRoute): ActivatedRoute {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute;
  }
}

// Add global declarations if needed
declare global {
  interface Window {
    _wq: any;
    Wistia: any;
    mdc: any;
    snackBar: any;
    installEvent: any;
    fullScreen: boolean;
  }
  interface Document {
    msFullscreenElement: any,
    msExitFullscreen: () => void;
    mozCancelFullScreen: () => void;
    webkitExitFullscreen: () => void;
    fullscreenElement: any;
    mozFullScreenElement: any;
    webkitFullscreenElement: any;
  }
  interface HTMLElement {
    webkitRequestFullscreen: () => void;
    msRequestFullscreen: () => void;
    mozRequestFullScreen: () => void;
  }
}